import React, { useState, useEffect } from "react";
import naturalCompare from "natural-compare";
import { Icon, Button } from "@blueprintjs/core";
import numeral from "numeral";
import moment from "moment";
import { ExportToCsv } from "export-to-csv";

const EMPTY_LIST = [];

export default function StaticTable({ fields, items, hideExport }) {
  const [sortKey, setSortKey] = useState({
    key: fields[0].id,
    order: fields[0].defaultOrder || 1,
  });
  const [data, setData] = useState(EMPTY_LIST);

  useEffect(() => {
    const filtered = items.slice();
    const field = fields.find((f) => f.id === sortKey.key);
    filtered.sort((a, b) => {
      const aa = (
        (field.accessor || defaultAccessor)(a, field) + ""
      ).toLowerCase();
      const bb = (
        (field.accessor || defaultAccessor)(b, field) + ""
      ).toLowerCase();
      return naturalCompare(aa, bb) * sortKey.order;
    });
    setData(filtered);
  }, [sortKey, items, fields]);

  if (!items) {
    return;
  }

  const showExport = !hideExport;

  const columns = [];

  for (const field of fields) {
    columns.push(
      <th key={"table-head-" + field.id}>
        {makeSortHeader(field.id, field.name)}
      </th>
    );
  }

  return (
    <>
      <table className={"bp3-html-table bp3-small bp3-html-table-striped"}>
        <thead className="print-hide">
          <tr>
            <td colSpan={fields.length}>
              {showExport && (
                <span style={{ float: "right" }}>
                  <Button
                    minimal
                    icon="cloud-download"
                    onClick={(e) => {
                      exportData(data, fields);
                    }}
                  >
                    Export
                  </Button>
                </span>
              )}
              {`${data.length} rows`}
            </td>
          </tr>
        </thead>
        <thead>
          <tr>{columns}</tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={"table-row-" + index}>
              {fields.map((field) => {
                const Renderer = field.renderer || DefaultRenderer;
                return (
                  <td style={cellStyle(field)}>
                    <Renderer
                      value={(field.accessor || defaultAccessor)(row, field)}
                      row={row}
                      field={field}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );

  function makeSortHeader(key, label) {
    return (
      <a
        href="#sort"
        onClick={(e) => {
          e.preventDefault();
          if (key === sortKey.key) {
            setSortKey({ key, order: -sortKey.order });
          } else {
            setSortKey({ key, order: 1 });
          }
        }}
      >
        {label}{" "}
        {key === sortKey.key && (
          <span className="print-hide">
            <Icon
              icon={
                sortKey.order === 1
                  ? "sort-alphabetical"
                  : "sort-alphabetical-desc"
              }
            />
          </span>
        )}
      </a>
    );
  }
}

function exportData(data, fields) {
  const exportData = data.map((row) => {
    const out = {};
    fields.forEach((field) => {
      out[field.name] = (field.accessor || defaultAccessor)(row, field) || "";
    });
    return out;
  });

  const options = {
    fieldSeparator: ",",
    quoteStrings: '"',
    decimalSeparator: ".",
    showLabels: true,
    useTextFile: false,
    useBom: true,
    useKeysAsHeaders: true,
    filename: `Export ${moment().toISOString()}`,
  };

  const csvExporter = new ExportToCsv(options);
  csvExporter.generateCsv(exportData);
}

function cellStyle(field) {
  if (field.type === "number") {
    return {
      textAlign: "right",
    };
  }
  return {};
}

function defaultAccessor(row, field) {
  let data = row[field.id];

  if (field.type === "email" && data) {
    return data.toLowerCase();
  }

  if (field.type === "number") {
    return data || 0;
  }

  return data;
}

function formatNumber(data) {
  let formatted = [];
  if (data.length > 4) {
    formatted.push(data.substring(0, 4));
    data = data.substring(4);
  }
  while (data.length > 0) {
    formatted.push(data.substring(0, 3));
    data = data.substring(3);
  }
  data = formatted.join("\u00A0");
  return data;
}

function DefaultRenderer({ value, field }) {
  if (field.type === "mobile" && value) {
    return formatNumber(value);
  }
  if (field.type === "number") {
    return numeral(value).format(field.format || "0,000");
  }
  if (field.type === "datetime") {
    if (!value || value === "0000-00-00 00:00:00") {
      return "-";
    }
    const parsed = moment(value);
    const today = moment();

    if (today.diff(parsed, "days") < 30) {
      return <span title={parsed.format("llll")}>{parsed.fromNow()}</span>;
    }

    return parsed.format("llll");
  }
  return value || "";
}
